import { type Href, router } from 'expo-router';
import { BellRing } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Button } from '@/components/Button';
import { Heading } from '@/components/Heading';
import { Text } from '@/components/Text';
import { colors, radii, spacing } from '@/constants/theme';
import {
  getPushPermission,
  markPushPrimed,
  registerForPushNotificationsAsync,
} from '@/services/pushNotifications';

/**
 * One-time push priming, presented as a full-screen MODAL over the home tab
 * (app/(tabs)/index.tsx triggers it on first view; registered with
 * presentation: 'fullScreenModal' in the root layout). It never navigates
 * anywhere — it overlays home and dismisses back to it — so it can't land in
 * or interrupt any other flow (notably a referred user's SoFi deal).
 * Explaining the value BEFORE firing the OS prompt is the point: tapping
 * "Turn on" is what triggers the system dialog, so "Not now" never burns the
 * one-shot iOS prompt. Either choice marks the device primed; users can still
 * enable later from Profile.
 */
export default function NotificationsPrimingScreen() {
  const [busy, setBusy] = useState(false);

  // Dismiss the modal — reveal the screen underneath (home). No redirect.
  function done() {
    if (router.canGoBack()) router.back();
    else router.replace('/(tabs)' as Href);
  }

  // Already answered (granted) or previously primed → skip the screen entirely.
  // Guards reinstalls and any accidental double-route.
  useEffect(() => {
    let active = true;
    void (async () => {
      const status = await getPushPermission();
      if (!active) return;
      if (status === 'granted') {
        await markPushPrimed();
        done();
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  async function enable() {
    if (busy) return;
    setBusy(true);
    try {
      await registerForPushNotificationsAsync({ promptIfNeeded: true });
    } finally {
      await markPushPrimed();
      done();
    }
  }

  async function skip() {
    await markPushPrimed();
    done();
  }

  return (
    <SafeAreaView style={styles.safe} edges={['top', 'bottom']}>
      <View style={styles.body}>
        <View style={styles.iconWrap}>
          <BellRing size={40} color={colors.primary} />
        </View>
        <Heading size="4xl" style={styles.headingCenter}>
          Get notified when you get paid
        </Heading>
        <Text variant="body" color="textMuted" align="center" style={styles.subtitle}>
          We’ll ping you the moment a referral cashes in and when you hit a
          milestone. No spam — just the good news.
        </Text>
      </View>

      <View style={styles.actions}>
        <Button
          label="Turn on notifications"
          variant="primary"
          fullWidth
          loading={busy}
          onPress={enable}
        />
        <Pressable
          onPress={skip}
          disabled={busy}
          accessibilityRole="button"
          accessibilityLabel="Not now"
          style={styles.skip}
        >
          <Text variant="bodyMedium" color="textMuted">
            Not now
          </Text>
        </Pressable>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: colors.background,
    padding: spacing.xl,
    justifyContent: 'space-between',
  },
  body: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.base,
  },
  iconWrap: {
    width: 88,
    height: 88,
    borderRadius: radii.full,
    backgroundColor: colors.accentTint,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.sm,
  },
  headingCenter: {
    textAlign: 'center',
  },
  subtitle: {
    maxWidth: 320,
  },
  actions: {
    gap: spacing.base,
  },
  skip: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.sm,
    minHeight: 44,
  },
});
